import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  MaintenanceWindowPreviewPatchManagementWebComponentSchema,
  MaintenanceWindowPreviewPatchManagementWebComponentTemplateData,
  maintenanceWindowPreviewPatchManagementWebComponentTemplateDataEmptyValue,
  POSTPONE_REBOOT_TYPE,
} from '../types';
import { htmlEncode } from '../../../utils/escape.utils';
import { TemplateState } from '../../../types/common/types';
import { OldSchedulerHelper } from 'apps/webcomponents/src/utils/maintenanceWindow/patchManagement/OldSchedulerHelper';
import { NewSchedulerHelper } from 'apps/webcomponents/src/utils/maintenanceWindow/patchManagement/NewSchedulerHelper';
import { RecurrenceOptions } from 'apps/webcomponents/src/types/common/schedulerTypes';
import { padHoursAndMinutes } from 'apps/webcomponents/src/utils/padHoursAndMinutes.utils';

export class MaintenanceWindowPreviewPatchManagementView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    this.dom.$errorContainer = this.shadow.querySelector('gz-image-repo[state="error"]');

    this.dom.$title = this.shadow.querySelector('#title');
    this.dom.$titleMaintenanceDetails = this.shadow.querySelector('#title-maintenance-details');

    this.dom.$targetedOperationsLabel = this.shadow.querySelector('#operations-targeted');
    this.dom.$targetedOperationsValue = this.shadow.querySelector('#operations-targeted gz-text');

    this.dom.$patchScopeLabel = this.shadow.querySelector('#patch-scope');
    this.dom.$patchScopeValue = this.shadow.querySelector('#patch-scope gz-text');

    this.dom.$recurrenceLabel = this.shadow.querySelector('#recurrence');
    this.dom.$recurrenceValue = this.shadow.querySelector('#recurrence gz-text');

    this.dom.$rebootLabel = this.shadow.querySelector('#reboot');
    this.dom.$rebootValue = this.shadow.querySelector('#reboot gz-text');

    // Setup translations
    this.getTranslations();
    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$errorContainer.innerText = this.dependencies.translateFn('Could not load maintenance window details');

    this.dom.$targetedOperationsLabel.label = htmlEncode(this.dependencies.translateFn('Targeted operations'));
    this.dom.$patchScopeLabel.label = htmlEncode(this.dependencies.translateFn('Patch scope'));
    this.dom.$recurrenceLabel.label = htmlEncode(this.dependencies.translateFn('Recurrence'));
    this.dom.$rebootLabel.label = htmlEncode(this.dependencies.translateFn('Reboot'));
  }

  buildSuccessView(data: MaintenanceWindowPreviewPatchManagementWebComponentSchema | null) {
    if (data === null) {
      return;
    }

    this.setTempateValues(this.convertToTemplateData(data));
  }

  buildErrorView(error: Error) {
    // Set error
    this.setErrorState();

    // Log error
    console.error(error);
  }

  convertToTemplateData(
    data: MaintenanceWindowPreviewPatchManagementWebComponentSchema
  ): MaintenanceWindowPreviewPatchManagementWebComponentTemplateData {
    return {
      name: data.name,
      targetedOperations: this.getTargetedOperations(data),
      patchScope: this.getPatchScope(data),
      recurrence: this.getRecurrence(data),
      reboot: this.getReboot(data),
    };
  }

  getTargetedOperations(data: MaintenanceWindowPreviewPatchManagementWebComponentSchema): string {
    const targetedOperations: string[] = [];

    if (data.specifics?.scanPatchesSettings?.enabled) {
      targetedOperations.push(this.dependencies.translateFn('Scan for patches'));
    }

    if (data.specifics?.installPatchesSettings?.enabled) {
      targetedOperations.push(this.dependencies.translateFn('Apply patches'));
    }

    return targetedOperations.join(', ');
  }

  getPatchScope(data: MaintenanceWindowPreviewPatchManagementWebComponentSchema): string {
    const patchScope: string[] = [];

    if (data.specifics?.installPatchesSettings?.enabled) {
      if (data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.enabled) {
        patchScope.push(this.dependencies.translateFn('Security'));
      }

      if (data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.enabled) {
        patchScope.push(this.dependencies.translateFn('Non-security'));
      }

      if (data.specifics?.installPatchesSettings?.updateType?.manuallyApprovedPatchesSettings?.enabled) {
        patchScope.push(this.dependencies.translateFn('Manually approved'));
      }
    }

    return patchScope.join(', ');
  }

  getRecurrence(data: MaintenanceWindowPreviewPatchManagementWebComponentSchema): string {
    let schedulerCount = 0;

    if (data.specifics?.scanPatchesSettings?.enabled) {
      schedulerCount += 1;
    }

    if (!data.specifics?.scanPatchesSettings?.useSameSchedule) {
      const useFallbackSchedule = data.specifics?.scanPatchesSettings?.useFallbackSchedule;

      if (data.specifics?.installPatchesSettings?.enabled) {
        const securityPatchesEnabled =
          data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.enabled;
        const securityPatchesScheduler = useFallbackSchedule
          ? data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.oldScheduler
          : data.specifics?.installPatchesSettings?.updateType?.securityPatchesSettings?.scheduler;

        if (securityPatchesEnabled && securityPatchesScheduler?.recurrence !== RecurrenceOptions.IMMEDIATELY) {
          schedulerCount += 1;
        }

        const nonSecurityPatchesEnabled =
          data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.enabled;
        const nonSecurityPatchesScheduler = useFallbackSchedule
          ? data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.oldScheduler
          : data.specifics?.installPatchesSettings?.updateType?.nonSecurityPatchesSettings?.scheduler;

        if (nonSecurityPatchesEnabled && nonSecurityPatchesScheduler?.recurrence !== RecurrenceOptions.IMMEDIATELY) {
          schedulerCount += 1;
        }
      }
    }

    if (schedulerCount === 0) {
      return this.dependencies.translateFn('Disabled');
    }

    if (schedulerCount > 1) {
      return this.dependencies.translateFn('{scheduleCount} schedules', {
        scheduleCount: schedulerCount,
      });
    }

    let description = '';

    if (data.specifics?.scanPatchesSettings?.useFallbackSchedule) {
      // Use old scheduler
      const helper = new OldSchedulerHelper();
      helper.injectDependencies(this.dependencies);
      description = helper.getShortDescription(data.specifics?.scanPatchesSettings?.oldScheduler);
    } else {
      // Use new scheduler
      const helper = new NewSchedulerHelper();
      helper.injectDependencies(this.dependencies);
      description = helper.getShortDescription(data.specifics?.scanPatchesSettings?.scheduler);
    }

    return description;
  }

  getReboot(data: MaintenanceWindowPreviewPatchManagementWebComponentSchema): string {
    let description = '';

    if (!data.specifics?.installPatchesSettings?.enabled) {
      return description;
    }

    const postponeReboot = data.specifics?.installPatchesSettings?.postponeReboot;

    if (!postponeReboot) {
      return description;
    }

    const rebootOption = postponeReboot?.option;

    switch (rebootOption) {
      case POSTPONE_REBOOT_TYPE.TIME:
        description = this.dependencies.translateFn('Users can postpone the reboot until a more convenient time');

        break;
      case POSTPONE_REBOOT_TYPE.BETWEEN:
        description = this.dependencies.translateFn(
          'Users can postpone the reboot only between {startTime} and {endTime}',
          {
            startTime: padHoursAndMinutes`${postponeReboot?.between?.startHour}:${postponeReboot?.between?.startMinute}`,
            endTime: padHoursAndMinutes`${postponeReboot?.between?.endHour}:${postponeReboot?.between?.endMinute}`,
          }
        );

        break;
      case POSTPONE_REBOOT_TYPE.AFTER:
        description = this.dependencies.translateFn('System will reboot automatically after {afterMinutes} minutes', {
          afterMinutes: postponeReboot?.afterMinutes,
        });

        break;
      default:
        break;
    }

    return description;
  }

  setTempateValues(data: MaintenanceWindowPreviewPatchManagementWebComponentTemplateData) {
    if (data.name !== '') {
      this.dom.$title.innerText = this.dependencies.translateFn('Summary {maintenanceWindowName}', {
        maintenanceWindowName: data.name,
      });
    } else {
      this.dom.$title.innerText = this.dependencies.translateFn('Maintenance Window Summary');
    }

    if (data.targetedOperations !== '') {
      this.dom.$titleMaintenanceDetails.innerText = this.dependencies.translateFn(
        'Schedule details for {targetedOperations}',
        {
          targetedOperations: data.targetedOperations,
        }
      );
    } else {
      this.dom.$titleMaintenanceDetails.innerText = this.dependencies.translateFn('Schedule details');
    }

    this.dom.$targetedOperationsValue.innerText = data.targetedOperations || this.dependencies.translateFn('None');

    this.dom.$patchScopeValue.innerText = data.patchScope || this.dependencies.translateFn('None');

    this.dom.$recurrenceValue.innerText = data.recurrence;

    this.dom.$rebootValue.innerText = data.reboot || this.dependencies.translateFn('N/A');
  }

  clearTemplateValues() {
    this.setTempateValues(maintenanceWindowPreviewPatchManagementWebComponentTemplateDataEmptyValue);
  }

  setLoadingState() {
    this.removeAllStates();
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  setErrorState() {
    this.removeAllStates();
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  removeAllStates() {
    this.resetTemplateState(this.dom.$wrapper);
  }
}
